import { FunctionComponent } from 'react'
import styled, { css } from 'styled-components'
import useRenderer from '@hooks/useRenderer'
import {
  MultilineWebarchiveContent,
  MultilineWebarchiveSidebarContent,
} from '@widgets/Multiline/types'
import { desktopCSS, mobileCSS, tabletCSS } from '@measures/responsive'
import Title from '@components/Webarchive/Title'
import Navigation from '@components/Webarchive/Navigation'

interface WebarchiveMultilineProps {
  children: MultilineWebarchiveContent
}

const StyledWebarchiveContent = styled.div`
  ${() => css`
    ${desktopCSS(css`
      grid-column-end: span 4;
      padding: 0;
      margin: 45px 0;
    `)}

    ${tabletCSS(css`
      grid-column: span 6;
      padding: 20px;
      margin: 20px 0;
    `)} 

    ${mobileCSS(css`
      grid-column: span 6;
      padding: 12px;
      margin: 0;
    `)}
  `}
`

const StyledWebarchiveSidebar = styled.div`
  display: grid;
  grid-gap: 20px;
  align-content: flex-start;

  ${desktopCSS(css`
    grid-column: span 2;
    grid-template-columns: minmax(0, 1fr);
    padding: 160px 0;
  `)}

  ${tabletCSS(css`
    grid-column: span 6;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    padding: 20px;
  `)} 

  ${mobileCSS(css`
    grid-column: span 6;
    grid-template-columns: minmax(0, 1fr);
    padding: 12px;
  `)}
`

const StyledWebarchiveMultiline = styled.div`
  display: grid;
  grid-gap: 20px;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  grid-column: span 6;
`

const TeaserWrapper = styled.div`
  margin: 10px 0;
`

const flattenWebarchiveSidebar = (
  layoutItem: MultilineWebarchiveSidebarContent
): Record<string, unknown> => layoutItem.content[0].content[0]

const WebarchiveMultiline: FunctionComponent<WebarchiveMultilineProps> = ({
  children,
}) => {
  const Renderer = useRenderer()

  const mainContent = children[0].content[0].content?.map((item, index) => (
    <TeaserWrapper key={`webarchive-teaser-${index}`}>
      <Renderer>{item.content[0]}</Renderer>
    </TeaserWrapper>
  ))

  const { title, date } = children[0].content[0]

  const sidebarContent = (
    <Renderer>{flattenWebarchiveSidebar(children[1])}</Renderer>
  )

  return (
    <StyledWebarchiveMultiline>
      <StyledWebarchiveContent>
        <Title>{title}</Title>
        <Navigation date={date} />
        {mainContent}
      </StyledWebarchiveContent>
      <StyledWebarchiveSidebar>{sidebarContent}</StyledWebarchiveSidebar>
    </StyledWebarchiveMultiline>
  )
}

export default WebarchiveMultiline
